import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/auth'

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15_000,
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

let refreshing: Promise<string | null> | null = null

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing
  refreshing = (async () => {
    try {
      const res = await axios.post<{ accessToken: string }>('/api/auth/refresh', {}, { withCredentials: true })
      const newToken = res.data.accessToken
      useAuthStore.getState().setAccessToken(newToken)
      return newToken
    } catch {
      useAuthStore.getState().clear()
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true
      const newToken = await tryRefresh()
      if (newToken) {
        original.headers?.set('Authorization', `Bearer ${newToken}`)
        return api.request(original)
      }
    }
    return Promise.reject(error)
  },
)

/** Extrage mesajul de eroare din răspunsul API (convenția { error: { message } }). */
export function apiErrorMessage(err: unknown, fallback = 'A apărut o eroare'): string {
  const ax = err as AxiosError<{ error?: { message?: string } }>
  const dinServer = ax.response?.data?.error?.message
  if (dinServer) return dinServer
  // Fara asta, o operatie care depaseste `timeout` arata identic cu o eroare de server:
  // raspunsul lipseste, deci nu are ce mesaj sa scoata. S-a intamplat la incarcarea
  // cursurilor BNR, unde sase ani treceau de 15 secunde (Alexandru, 25.09.2026).
  if (ax.code === 'ECONNABORTED' || ax.code === 'ETIMEDOUT') {
    return 'Serverul nu a răspuns în timpul alocat. Operația poate continua acolo — mai încearcă peste un minut.'
  }
  if (ax.code === 'ERR_NETWORK') return 'Nu am putut contacta serverul. Verifică legătura la internet.'
  // `downloadFile` citeste motivul din Blob-ul de eroare si arunca un Error obisnuit.
  // Fara ramura asta mesajul lui se pierdea aici, si tot „A aparut o eroare" se vedea —
  // desi serverul spusese exact ce lipseste (Alexandru, 25.09.2026).
  if (err instanceof Error && err.message) return err.message
  return fallback
}
